import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';

function ReminderTest() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [customEmail, setCustomEmail] = useState('');
  const [customName, setCustomName] = useState('');
  const [appointments, setAppointments] = useState([]);
  const [selectedAppointment, setSelectedAppointment] = useState('');
  const [loadingAppointments, setLoadingAppointments] = useState(false);

  // Lade alle Termine für die Auswahl
  const loadAppointments = async () => {
    setLoadingAppointments(true);
    try {
      const { data, error } = await supabase
        .from('appointments')
        .select('*')
        .order('date', { ascending: true });

      if (error) throw error;
      setAppointments(data || []);
    } catch (err) {
      setError('Fehler beim Laden der Termine: ' + err.message);
    } finally {
      setLoadingAppointments(false);
    }
  };

  // Test-E-Mail an spezifischen Benutzer senden
  const sendTestToSpecificUser = async () => {
    if (!customEmail || !customName) {
      setError('Bitte geben Sie E-Mail und Name ein');
      return;
    }

    setIsLoading(true);
    setError('');
    setResult(null);

    try {
      const response = await fetch('/api/test-specific-reminder', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: customEmail,
          name: customName,
          appointmentId: selectedAppointment
        }),
      });

      // Überprüfe ob die Antwort gültig ist
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();

      if (response.ok) {
        setResult({
          success: true,
          message: '✅ Test-E-Mail erfolgreich gesendet!',
          details: `E-Mail gesendet an: ${customEmail}`,
          recipient: customEmail
        });
      } else {
        setError(data.error || 'Fehler beim Senden der Test-E-Mail');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Lade Termine beim ersten Laden
  useEffect(() => {
    loadAppointments();
  }, []);

  // Sende Erinnerung an den nächsten Termin
  const sendReminderToNextAppointment = async () => {
    setIsLoading(true);
    setError('');
    setResult(null);

    try {
      const response = await fetch('/api/send-next-reminder', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      // Überprüfe ob die Antwort gültig ist
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();

      if (response.ok) {
        setResult({
          success: true,
          message: '✅ Erinnerungs-E-Mail an nächsten Termin gesendet!',
          details: data.details || `E-Mail gesendet an: ${data.recipient}`,
          recipient: data.recipient,
          appointment: data.appointment
        });
      } else {
        setError(data.error || 'Fehler beim Senden der Erinnerungs-E-Mail');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const testReminderSystem = async () => {
    setIsLoading(true);
    setError('');
    setResult(null);

    try {
      // Verwende die echte API für Tests
      const response = await fetch('/api/test-reminders', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      // Überprüfe ob die Antwort gültig ist
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();

      if (response.ok) {
        setResult({
          success: true,
          message: '✅ Test der Erinnerungs-E-Mails abgeschlossen!',
          details: `Gefundene Termine: ${data.appointmentsFound}, Erfolgreich: ${data.summary.successful}, Fehler: ${data.summary.errors}`,
          testResults: data.testResults,
          summary: data.summary
        });
      } else {
        setError(data.error || 'Fehler beim Testen der Erinnerungs-E-Mails');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const sendRemindersNow = async () => {
    setIsLoading(true);
    setError('');
    setResult(null);

    try {
      // Verwende die echte API für Erinnerungen
      const response = await fetch('/api/send-reminders', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      // Überprüfe ob die Antwort gültig ist
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();

      if (response.ok) {
        setResult({
          success: true,
          message: '✅ Erinnerungs-E-Mails verarbeitet!',
          details: `Gefundene Termine: ${data.appointmentsFound}, E-Mails gesendet: ${data.emailsSent}`,
          appointmentsFound: data.appointmentsFound,
          emailsSent: data.emailsSent
        });
      } else {
        setError(data.error || 'Fehler beim Senden der Erinnerungs-E-Mails');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900 flex items-center justify-center p-4">
      <div className="w-full max-w-4xl mx-auto">
        <div className="bg-zinc-800 border border-zinc-700 rounded-2xl shadow-2xl overflow-hidden">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-3xl font-bold text-zinc-100">Erinnerungs-E-Mail System</h1>
              <button
                onClick={() => navigate('/admin')}
                className="flex items-center justify-center w-10 h-10 text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700/50 rounded-lg transition-all duration-200"
                title="Zurück zum Dashboard"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-6">
              {/* Spezifischer Test */}
              <div className="bg-zinc-700/50 border border-zinc-600 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-zinc-100 mb-4 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                  </svg>
                  Test an spezifischen Benutzer
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">
                      E-Mail-Adresse
                    </label>
                    <input
                      type="email"
                      value={customEmail}
                      onChange={(e) => setCustomEmail(e.target.value)}
                      placeholder="test@example.com"
                      className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-zinc-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">
                      Name
                    </label>
                    <input
                      type="text"
                      value={customName}
                      onChange={(e) => setCustomName(e.target.value)}
                      placeholder="Max Mustermann"
                      className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-zinc-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-zinc-300 mb-2">
                    Termin auswählen (optional)
                  </label>
                  <select
                    value={selectedAppointment}
                    onChange={(e) => setSelectedAppointment(e.target.value)}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-zinc-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={loadingAppointments}
                  >
                    <option value="">-- Kein spezifischer Termin --</option>
                    {appointments.map((appointment) => (
                      <option key={appointment.id} value={appointment.id}>
                        {new Date(appointment.date).toLocaleDateString("de-DE")} - {appointment.time} - {appointment.name} ({appointment.email})
                      </option>
                    ))}
                  </select>
                  {loadingAppointments && (
                    <p className="text-sm text-zinc-400 mt-1">Lade Termine...</p>
                  )}
                </div>

                <button
                  onClick={sendTestToSpecificUser}
                  disabled={isLoading || !customEmail || !customName}
                  className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-zinc-700 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Sende...</span>
                    </>
                  ) : (
                    <>
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                      </svg>
                      <span>Test-E-Mail senden</span>
                    </>
                  )}
                </button>
              </div>

              {/* Nächster Termin Button */}
              <div className="bg-orange-900/20 border border-orange-500/30 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-orange-100 mb-4 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Nächster Termin
                </h3>
                <p className="text-orange-200 text-sm mb-4">
                  Senden Sie sofort eine Erinnerungs-E-Mail an den nächsten bevorstehenden Termin.
                </p>
                <button
                  onClick={sendReminderToNextAppointment}
                  disabled={isLoading}
                  className="w-full bg-orange-600 hover:bg-orange-700 disabled:bg-zinc-700 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Sende...</span>
                    </>
                  ) : (
                    <>
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Erinnerung an nächsten Termin senden</span>
                    </>
                  )}
                </button>
              </div>

              {/* Test Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={testReminderSystem}
                  disabled={isLoading}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-zinc-700 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Teste...</span>
                    </>
                  ) : (
                    <>
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                      <span>System testen</span>
                    </>
                  )}
                </button>

                <button
                  onClick={sendRemindersNow}
                  disabled={isLoading}
                  className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-zinc-700 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Sende...</span>
                    </>
                  ) : (
                    <>
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                      <span>Erinnerungen jetzt senden</span>
                    </>
                  )}
                </button>
              </div>

              {/* Error Display */}
              {error && (
                <div className="p-4 bg-red-900/20 border border-red-500/30 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <svg className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-red-300">{error}</p>
                  </div>
                </div>
              )}

              {/* Result Display */}
              {result && (
                <div className="p-4 bg-green-900/20 border border-green-500/30 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <svg className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="text-green-300">
                      <p className="font-medium mb-2">{result.message}</p>
                      <p className="text-sm mb-3">{result.details}</p>
                      
                      {result.testResults && result.testResults.length > 0 && (
                        <div>
                          <p className="font-medium mb-2">📋 Test-Ergebnisse:</p>
                          <div className="space-y-2 max-h-60 overflow-y-auto">
                            {result.testResults.map((test, index) => (
                              <div key={index} className="bg-zinc-800 p-3 rounded border border-zinc-600">
                                <div className="flex justify-between items-start">
                                  <div>
                                    <p className="font-medium">{test.appointment}</p>
                                    <p className="text-sm text-zinc-400">
                                      {new Date(test.date).toLocaleDateString("de-DE")} um {test.time}
                                    </p>
                                    {test.email && (
                                      <p className="text-sm text-zinc-400">{test.email}</p>
                                    )}
                                  </div>
                                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                                    test.status === 'SUCCESS' 
                                      ? 'bg-green-600 text-green-100' 
                                      : 'bg-red-600 text-red-100'
                                  }`}>
                                    {test.status}
                                  </span>
                                </div>
                                <p className="text-sm mt-1">{test.message}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Info Box */}
              <div className="p-4 bg-blue-900/20 border border-blue-500/30 rounded-lg">
                <div className="flex items-start space-x-3">
                  <svg className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="text-blue-300">
                    <p className="font-medium mb-2 flex items-center">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Erinnerungs-E-Mail System (Vercel Pro)
                    </p>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-start space-x-2">
                        <svg className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span><strong>Nächster Termin:</strong> Sofortige Erinnerung an den nächsten bevorstehenden Termin</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <svg className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                        </svg>
                        <span><strong>Spezifischer Test:</strong> Senden Sie Test-E-Mails an beliebige E-Mail-Adressen</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <svg className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                        <span><strong>System-Test:</strong> Testet alle Termine der nächsten 24 Stunden</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <svg className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                        </svg>
                        <span><strong>Sofort senden:</strong> Sendet Erinnerungen für Termine in der nächsten Stunde</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <svg className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span><strong>Automatisch:</strong> Stündliche Cron-Jobs senden Erinnerungen 1 Stunde vorher</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <svg className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <span><strong>E-Mail:</strong> info@feltes-solutions.de (Strato SMTP)</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ReminderTest;
